import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { textHighlightingService, type HighlightItem } from '@/lib/services/TextHighLightingService';
import { highlightDataService } from '@/lib/services/HighlightDataService';
import { highlightVisibilityService } from '@/lib/services/HighlightVisibilityService';
import { tagsStore } from '@/lib/stores/tagsStore';
import { typesStore } from '@/lib/stores/typesStore';
import { objectsStore } from "@/lib/stores/objectsStore";
import { getSelectedGame, setSelectedGame } from '@/lib/stores/unifiedUserStore';

interface TextHighlightingContextType {
  highlightItems: HighlightItem[];
  addHighlightItems: (items: HighlightItem[]) => void;
  removeHighlightItem: (itemId: string) => void;
  clearAllHighlights: () => void;
  refreshHighlightItems: () => void;
  registerHighlightData: (source: string, items: HighlightItem[], priority?: number) => void;
  unregisterHighlightData: (source: string) => void;
  getStatistics: () => ReturnType<typeof highlightDataService.getStatistics>;
}

const TextHighlightingContext = createContext<TextHighlightingContextType | undefined>(undefined);

export const useTextHighlighting = () => {
  const context = useContext(TextHighlightingContext);
  if (context === undefined) {
    throw new Error('useTextHighlighting must be used within a TextHighlightingProvider');
  }
  return context;
};

interface TextHighlightingProviderProps {
  children: ReactNode;
}

export const TextHighlightingProvider: React.FC<TextHighlightingProviderProps> = ({ children }) => {
  const [highlightItems, setHighlightItems] = useState<HighlightItem[]>([]);
  
  const refreshHighlightItems = useCallback(() => {
    // Only refresh highlight items if a game is selected
    const selectedGame = getSelectedGame();
    if (!selectedGame?.id) {
      setHighlightItems([]);
      return;
    }
    
    // Get items from both the highlighting service and the data service
    const serviceItems = textHighlightingService.getHighlightItems();
    const dataServiceItems = highlightDataService.getRegisteredHighlightItems();
    
    // Combine all items from both services (no deduplication needed)
    // NOTE: We keep ALL items here (including hidden ones) so the sidebar can show them
    // The filtering happens in the text highlight manager when actually applying highlights
    const combinedItems = [...serviceItems, ...dataServiceItems];
    
    setHighlightItems(combinedItems);
  }, []);

  // Load initial highlight items from the service
  useEffect(() => {
    refreshHighlightItems();
  }, [refreshHighlightItems]);

  // Subscribe to store changes to refresh highlights when data changes
  useEffect(() => {
    const unsubscribeFromTags = tagsStore.subscribe(() => {
      refreshHighlightItems();
    });

    const unsubscribeFromTypes = typesStore.subscribe(() => {
      refreshHighlightItems();
    });

    const unsubscribeFromGlossary = objectsStore.subscribe(() => {
      refreshHighlightItems();
    });

    // Subscribe to visibility changes to refresh highlights when hidden/shown
    const unsubscribeFromVisibility = highlightVisibilityService.subscribe(() => {
      refreshHighlightItems();
    });

    // Subscribe to game selection changes to refresh highlights when game changes


    return () => {
      unsubscribeFromTags();
      unsubscribeFromTypes();
      unsubscribeFromGlossary();
      unsubscribeFromVisibility();
    };
  }, [refreshHighlightItems]);
  
  const addHighlightItems = useCallback((items: HighlightItem[]) => {
    textHighlightingService.addHighlightItems(items);
    refreshHighlightItems();
  }, [refreshHighlightItems]);
  
  const removeHighlightItem = useCallback((itemId: string) => {
    textHighlightingService.removeHighlight(itemId);
    refreshHighlightItems();
  }, [refreshHighlightItems]);
  
  const clearAllHighlights = useCallback(() => {
    textHighlightingService.clearAllHighlights();
    highlightDataService.clearAllRegisteredData();
    refreshHighlightItems();
  }, [refreshHighlightItems]);
  
  const registerHighlightData = useCallback((source: string, items: HighlightItem[], priority?: number) => {
    highlightDataService.registerHighlightData({
      items,
      source,
      priority
    });
    refreshHighlightItems();
  }, [refreshHighlightItems]);
  
  const unregisterHighlightData = useCallback((source: string) => {
    highlightDataService.unregisterHighlightData(source);
    refreshHighlightItems();
  }, [refreshHighlightItems]);
  
  const getStatistics = useCallback(() => {
    return highlightDataService.getStatistics();
  }, []);
  
  return (
    <TextHighlightingContext.Provider value={{
      highlightItems,
      addHighlightItems,
      removeHighlightItem,
      clearAllHighlights,
      refreshHighlightItems,
      registerHighlightData,
      unregisterHighlightData,
      getStatistics
    }}>
      {children}
    </TextHighlightingContext.Provider>
  );
};
